import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { useDataLayer } from "../../../../Contexts/DataLayer.context.jsx";
import { useMask } from "../../../../Contexts/mask.context.jsx";
import { useNavBar } from "../../../../Contexts/navbar.items.context.jsx";
import { getUserFromToken } from "../../../../utils/functions.ts";
import DragBox2026 from "../../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";
import SimpleCalendar from "../../../Universals/simpleCalendar/simpleCalendar.component.jsx";
import { getTodayEasternYMD } from "../../../../utils/dateUtils.ts";
import {
  cflObject,
  eventSearch,
  CFLStatus,
  LoadUFL24,
  NFLStatus,
  nflObject,
  ufl24Object,
} from "../../../../utils/loadFunctions.ts";

const leagueOptions = ["CFL", "NFL", "UFL24", "Event"];
const BB_LEAGUE_STORAGE_KEY = "BBLeague";
const BB_DATE_STORAGE_KEY = "BBDate";

const getToday = () => getTodayEasternYMD();

const getDateOrToday = (value) => {
  const raw = String(value || "").trim();
  if (!raw || raw === "-" || raw === "0000-00-00" || raw.startsWith("0000-00-00")) return getToday();
  const dateOnly = raw.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : getToday();
};

const parseTransDate = (item) => {
  const raw = item?.ProTeamDate || item?.theDate || item?.transDate || "";
  const time = Date.parse(raw);
  return Number.isNaN(time) ? 0 : time;
};

const normalizeLeague = (league) => String(league || "").trim().toUpperCase();

const hasBluBarsAccess = (user) => {
  const securityLevel = String(user?.userContactLevel ?? user?.security ?? user?.ADMIN ?? user?.admin ?? "").trim();
  return securityLevel === "1" || user?.ADMIN === true || user?.admin === true;
};

const readStoredBBLeague = () => {
  try {
    const storedLeague = localStorage.getItem(BB_LEAGUE_STORAGE_KEY);
    return leagueOptions.includes(storedLeague) ? storedLeague : "";
  } catch {
    return "";
  }
};

const readStoredBBDate = () => {
  try {
    const storedDate = localStorage.getItem(BB_DATE_STORAGE_KEY);
    return getDateOrToday(storedDate);
  } catch {
    return getToday();
  }
};

const saveBBStorageValue = (key, value, canSave) => {
  if (!canSave) return;
  try {
    localStorage.setItem(key, value);
  } catch {}
};

const getTransactionID = (item) => item?.transactionID || item?.TransactionRowID || item?.ID || "";

const getTransactionRowKey = (item, idx) => getTransactionID(item) || `${item?.ProTeamDate || "transaction"}-${idx}`;

const getPlayerRowID = (item) => item?.FBXID || item?.DR_Num || item?.DRNUM || item?.drnum || "";

const cleanDisplayValue = (value) => {
  const text = String(value ?? "").trim();
  return text.toLowerCase() === "null" ? "" : text;
};

const isPlaceholderStatus = (status) => {
  const id = String(status?.status_id || "").trim();
  const label = String(status?.status || "").trim().toLowerCase();
  return !id || id === "-" || id === "--" || label === "status";
};

const STICKY_NOTE_STORAGE_KEY = "FBX_BLUE_TRANSACTION_NOTE_DRAFT";

const readStickyNote = () => {
  try {
    return localStorage.getItem(STICKY_NOTE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const buildTransactionEditForm = (item, league) => ({
  id: getTransactionID(item),
  league: normalizeLeague(item?.League || league) === "EVENT" ? "Event" : item?.League || league,
  emailCode: cleanDisplayValue(item?.emailCode || item?.EmailCode || ""),
  teamCode: item?.ProTeam || item?.A_ProTeam || "",
  statusCode: item?.ProTeamStatus || "",
  posCode: item?.PRO_POS || item?.A_ProPos || "",
  transDate: item?.ProTeamDate || getToday(),
  teamYear: item?.TeamYear || "",
  teamSide: item?.TeamSide || "",
  noteData: item?.ST_NOTES || "",
  pendingEmailCount: Number(item?.pendingEmailCount || 0),
  original: {
    emailCode: cleanDisplayValue(item?.emailCode || item?.EmailCode || ""),
    league: normalizeLeague(item?.League || league),
    teamCode: item?.ProTeam || item?.A_ProTeam || "",
    statusCode: item?.ProTeamStatus || "",
    posCode: item?.PRO_POS || item?.A_ProPos || "",
    transDate: item?.ProTeamDate || "",
    teamYear: item?.TeamYear || "",
    teamSide: item?.TeamSide || "",
    noteData: item?.ST_NOTES || "",
    pendingEmailCount: Number(item?.pendingEmailCount || 0),
  },
});

const getLeagueDefaults = (player, league) => {
  const prefixMap = {
    CFL: {
      team: player?.cflTeam,
      status: player?.cflStatus,
      pos: player?.cflPos || player?.colPos1,
      date: player?.cflDate,
      draftTeam: player?.cflDraftTeam,
      draftRound: player?.cflDraftRound,
      draftPick: player?.cflDraftRoundPick,
      finalContractYear: player?.optionYr,
    },
    NFL: {
      team: player?.nflTeam,
      status: player?.nflStatus,
      pos: player?.nflPos || player?.colPos1,
      date: player?.nflDate,
      draftTeam: player?.nflDraftTeam,
      draftRound: player?.nflDraftRound,
      draftPick: player?.nflDraftRoundPick,
    },
    UFL24: {
      team: player?.ufl24Team,
      status: player?.ufl24Status,
      pos: player?.ufl24Pos || player?.colPos1,
      date: player?.ufl24Date,
      draftTeam: player?.ufl24DraftTeam,
      draftRound: player?.ufl24DraftRound,
      draftPick: player?.ufl24DraftRoundPick,
    },
    Event: {
      team: player?.theEvent,
      status: player?.eventSide,
      pos: player?.eventPos || player?.colPos1,
      date: player?.theEventDate,
      eventTeam: player?.eventTeam,
    },
  };

  return prefixMap[league] || {
    team: "",
    status: "",
    pos: player?.colPos1 || "",
    date: getToday(),
    draftTeam: "",
    draftRound: "",
    draftPick: "",
  };
};

const BlueTransactions = () => {
  const { activePlayer, playerID, setActivePlayer, setPlayerData, setSavedOriginalPlayerData } = usePlayer();
  const { positionArray } = useDataLayer();
  const { mask, unmask } = useMask();
  const { updateActiveSideButton } = useNavBar();
  const [ufl24Teams, setUfl24Teams] = useState(() => (Array.isArray(ufl24Object) ? ufl24Object : []));
  const [canPersistBBPrefs, setCanPersistBBPrefs] = useState(false);
  const [form, setForm] = useState({
    league: readStoredBBLeague(),
    teamCode: "",
    statusCode: "",
    posCode: "",
    transDate: readStoredBBDate(),
    draftCode: "",
    draftRound: "",
    draftPick: "",
    finalContractYear: "",
    eventSide: "",
    eventTeam: "",
    saveMode: "Both",
    noteData: readStickyNote(),
  });
  const [preview, setPreview] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [topSaving, setTopSaving] = useState(false);
  const [topSaveMessage, setTopSaveMessage] = useState("");
  const [topSaveError, setTopSaveError] = useState("");
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [editingTransactionKey, setEditingTransactionKey] = useState("");
  const [editForm, setEditForm] = useState(null);
  const [editError, setEditError] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const leftColumnRef = useRef(null);
  const lastPlayerKeyRef = useRef("");
  const [transactionHeight, setTransactionHeight] = useState(350);
  const [editSaving, setEditSaving] = useState("");

  const playerName = useMemo(() => {
    const last = activePlayer?.last || activePlayer?.LAST || "";
    const first = activePlayer?.first || activePlayer?.FIRST || "";
    return [last, first].filter(Boolean).join(", ");
  }, [activePlayer]);

  const playerTitle = useMemo(() => {
    const school =
      activePlayer?.longSchool ||
      activePlayer?.LONGSCHOOL ||
      activePlayer?.schoolLong ||
      activePlayer?.SchoolLong ||
      activePlayer?.school ||
      activePlayer?.SCHOOL ||
      "";
    const position = activePlayer?.colPos1 || activePlayer?.COL_POS1 || "";
    const parts = [playerName || "No Player Selected", position, school].filter(Boolean);
    return parts.join(" - ");
  }, [activePlayer, playerName]);

  const activePlayerKey = useMemo(
    () => String(activePlayer?.FBXID || playerID || ""),
    [activePlayer?.FBXID, playerID]
  );

  const applyOneLineRow = useCallback(
    (oneLineRow) => {
      const rowID = getPlayerRowID(oneLineRow);
      if (!rowID) return;

      const patchRows = (rows) => {
        if (!Array.isArray(rows)) return rows;
        let changed = false;
        const nextRows = rows.map((row) => {
          if (String(getPlayerRowID(row)) !== String(rowID)) return row;
          changed = true;
          return { ...row, ...oneLineRow };
        });
        return changed ? nextRows : rows;
      };

      if (typeof setPlayerData === "function") {
        setPlayerData((prev) => patchRows(prev));
      }
      if (typeof setSavedOriginalPlayerData === "function") {
        setSavedOriginalPlayerData((prev) => patchRows(prev));
      }
      if (typeof setActivePlayer === "function") {
        setActivePlayer((prev) => {
          if (!prev || String(getPlayerRowID(prev)) !== String(rowID)) return prev;
          return { ...prev, ...oneLineRow };
        });
      }
    },
    [setActivePlayer, setPlayerData, setSavedOriginalPlayerData]
  );

  const closeBlueTransactions = useCallback(() => {
    if (typeof updateActiveSideButton === "function") {
      updateActiveSideButton("");
    }
  }, [updateActiveSideButton]);

  const filteredHistoryData = useMemo(() => {
    const selectedLeague = normalizeLeague(form.league);
    return historyData.filter((item) => normalizeLeague(item?.League) === selectedLeague);
  }, [historyData, form.league]);

  useEffect(() => {
    let isMounted = true;

    LoadUFL24()
      .then((teams) => {
        if (isMounted && Array.isArray(teams)) {
          setUfl24Teams(teams);
        }
      })
      .catch(() => {
        if (isMounted && Array.isArray(ufl24Object)) {
          setUfl24Teams(ufl24Object);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const teamOptions = useMemo(() => {
    switch (form.league) {
      case "NFL":
        return nflObject;
      case "UFL24":
        return ufl24Teams;
      case "Event":
        return eventSearch;
      case "CFL":
        return cflObject;
      default:
        return [];
    }
  }, [form.league, ufl24Teams]);

  const statusOptions = useMemo(() => {
    switch (form.league) {
      case "CFL":
        return CFLStatus;
      case "NFL":
      case "UFL24":
      case "Event":
        return NFLStatus;
      default:
        return [];
    }
  }, [form.league]);

  const visibleStatusOptions = useMemo(() => {
    return statusOptions.filter((status) => !isPlaceholderStatus(status));
  }, [statusOptions]);

  const selectedStatusLabel = useMemo(() => {
    const selectedStatus = String(form.statusCode || "").trim();
    if (!selectedStatus || selectedStatus === "-" || selectedStatus === "--") return "";
    const match = statusOptions.find((status) => String(status?.status_id || "").trim() === selectedStatus);
    if (match && isPlaceholderStatus(match)) return "";
    return match ? match.status : selectedStatus;
  }, [form.statusCode, statusOptions]);

  const positionOptions = useMemo(() => {
    const base = Array.isArray(positionArray) ? positionArray : [];
    return base
      .map((position) => {
        const code = String(position?.POS_SPEC_CODE || "").trim();
        const rawDesc = String(position?.POS_DESC || "").trim();
        const desc = rawDesc
          .replace(new RegExp(`^${code}\\s*-?\\s*`, "i"), "")
          .trim();
        if (!code) return null;
        return {
          code,
          label: desc || code,
        };
      })
      .filter(Boolean);
  }, [positionArray]);

  useEffect(() => {
    let isMounted = true;

    getUserFromToken()
      .then((user) => {
        if (isMounted) setCanPersistBBPrefs(hasBluBarsAccess(user));
      })
      .catch(() => {
        if (isMounted) setCanPersistBBPrefs(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const defaults = getLeagueDefaults(activePlayer, form.league);
    const storedDate = readStoredBBDate();
    setForm((prev) => ({
      ...prev,
      teamCode: defaults.team || "",
      statusCode: defaults.status || "",
      posCode: defaults.pos || "",
      transDate: storedDate,
      draftCode: defaults.draftTeam || "",
      draftRound: defaults.draftRound || "",
      draftPick: defaults.draftPick || "",
      finalContractYear: defaults.finalContractYear || "",
      eventSide: defaults.status || "",
      eventTeam: defaults.eventTeam || "",
    }));
  }, [activePlayer, form.league]);

  useEffect(() => {
    let isMounted = true;

    const loadHistory = async () => {
      const activeID = activePlayer?.FBXID || playerID;
      if (!activeID) {
        setHistoryData([]);
        setHistoryError("");
        return;
      }

      setHistoryLoading(true);
      setHistoryError("");

      try {
        const user = await getUserFromToken();
        const response = await fetch("https://www.fbxchange.com/ReactPHP/transactions.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ FBXID: activeID, user, script: "SearchHistory" }),
        });
        const data = await response.json();
        const sorted = (Array.isArray(data) ? data : [])
          .slice()
          .sort((a, b) => {
            const dateDiff = parseTransDate(b) - parseTransDate(a);
            if (dateDiff !== 0) return dateDiff;
            return Number(b?.ID || 0) - Number(a?.ID || 0);
          });

        if (!isMounted) return;
        setHistoryData(sorted);
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching BluBars transaction history:", error);
        setHistoryData([]);
        setHistoryError("Unable to load transaction history.");
      } finally {
        if (isMounted) setHistoryLoading(false);
      }
    };

    loadHistory();

    return () => {
      isMounted = false;
    };
  }, [activePlayer?.FBXID, playerID]);

  useEffect(() => {
    if (lastPlayerKeyRef.current === "") {
      lastPlayerKeyRef.current = activePlayerKey;
      return;
    }
    if (lastPlayerKeyRef.current === activePlayerKey) return;

    lastPlayerKeyRef.current = activePlayerKey;
    setEditingTransactionKey("");
    setEditForm(null);
    setEditError("");
    setEditMessage("");
    setTopSaveMessage("");
    setTopSaveError("");
  }, [activePlayerKey]);

  useEffect(() => {
    setEditingTransactionKey("");
    setEditForm(null);
    setEditError("");
    setEditMessage("");
    setTopSaveMessage("");
    setTopSaveError("");
  }, [form.league]);

  useEffect(() => {
    try {
      if (isDirty) mask();
      else unmask();
    } catch {}
  }, [isDirty, mask, unmask]);

  useEffect(() => {
    return () => {
      try {
        unmask();
      } catch {}
    };
  }, [unmask]);

  useEffect(() => {
    const leftColumn = leftColumnRef.current;
    if (!leftColumn) return undefined;

    const measureLeftColumn = () => {
      const nextHeight = Math.ceil(leftColumn.getBoundingClientRect().height);
      setTransactionHeight(Math.max(nextHeight, 260));
    };

    measureLeftColumn();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureLeftColumn);
      return () => window.removeEventListener("resize", measureLeftColumn);
    }

    const resizeObserver = new ResizeObserver(measureLeftColumn);
    resizeObserver.observe(leftColumn);
    window.addEventListener("resize", measureLeftColumn);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measureLeftColumn);
    };
  }, [form.league]);

  const handleChange = (field, value) => {
    setPreview(null);
    setTopSaveMessage("");
    setTopSaveError("");
    if (field === "noteData") {
      try {
        localStorage.setItem(STICKY_NOTE_STORAGE_KEY, value);
      } catch {}
    } else {
      setIsDirty(true);
    }
    if (field === "transDate") {
      saveBBStorageValue(BB_DATE_STORAGE_KEY, getDateOrToday(value), canPersistBBPrefs);
    }
    setForm((prev) => ({ ...prev, [field]: field === "transDate" ? getDateOrToday(value) : value }));
  };

  const handleClearNote = () => {
    setPreview(null);
    setTopSaveMessage("");
    setTopSaveError("");
    try {
      localStorage.removeItem(STICKY_NOTE_STORAGE_KEY);
    } catch {}
    setForm((prev) => ({ ...prev, noteData: "" }));
  };

  const handleLeagueChange = (value) => {
    setPreview(null);
    setTopSaveMessage("");
    setTopSaveError("");
    setIsDirty(false);
    saveBBStorageValue(BB_LEAGUE_STORAGE_KEY, value, canPersistBBPrefs);
    setForm((prev) => ({ ...prev, league: value }));
  };

  const buildPayload = (saveMode = form.saveMode) => ({
    script: "TransSpecialData",
    playerID: activePlayer?.FBXID || playerID || "",
    playerName: playerName || "No Player Selected",
    league: form.league,
    teamCode: form.teamCode,
    statusCode: form.league === "Event" ? form.eventSide : form.statusCode,
    posCode: form.posCode,
    transDate: getDateOrToday(form.transDate),
    draftCode: form.draftCode,
    draftRound: form.draftRound,
    draftPick: form.draftPick,
    fcyCFL: form.finalContractYear,
    eventSide: form.eventSide,
    eventTeam: form.eventTeam,
    whatButton: saveMode,
    noteData: form.noteData,
  });

  const handleSaveTopTransaction = async () => {
    await handleSaveTopMode("SaveCollegePttEmail", "Both");
  };

  const handleSaveTopMode = async (script, saveMode) => {
    const payload = {
      ...buildPayload(saveMode),
      script,
    };

    setPreview(null);
    setTopSaveMessage("");
    setTopSaveError("");
    setTopSaving(true);

    try {
      const user = await getUserFromToken();
      payload.user = user;
      payload.team = "FBX";

      const response = await fetch("https://www.fbxchange.com/ReactPHP/danTransactions.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!result?.success) {
        throw new Error(result?.error || "Unable to save transaction.");
      }

      if (result.transaction) {
        setHistoryData((prev) => [result.transaction, ...prev]);
      }
      applyOneLineRow(result.oneLineRow);
      saveBBStorageValue(BB_LEAGUE_STORAGE_KEY, form.league, canPersistBBPrefs);
      saveBBStorageValue(BB_DATE_STORAGE_KEY, getDateOrToday(form.transDate), canPersistBBPrefs);

      if (script === "SaveCollegeOnly") {
        setTopSaveMessage("Saved Current to College only.");
      } else if (script === "SaveHistoryOnly") {
        setTopSaveMessage("Saved History to PTT only.");
      } else {
        const queuedEmails = Number(result.queuedEmails || 0);
        const emailText =
          queuedEmails > 0
            ? ` and added ${queuedEmails} email transaction${queuedEmails === 1 ? "" : "s"}`
            : "";
        setTopSaveMessage(
          result.pttInserted
            ? `Updated College. Added to Pro Team Transactions${emailText}.`
            : "Updated College. No Pro Team Transactions row added."
        );
      }
      setIsDirty(false);
      closeBlueTransactions();
    } catch (error) {
      setTopSaveError(error.message || "Unable to save transaction.");
    } finally {
      setTopSaving(false);
    }
  };

  const handleCancel = () => {
    const defaults = getLeagueDefaults(activePlayer, form.league);
    setForm((prev) => ({
      ...prev,
      teamCode: defaults.team || "",
      statusCode: defaults.status || "",
      posCode: defaults.pos || "",
      transDate: readStoredBBDate(),
      draftCode: defaults.draftTeam || "",
      draftRound: defaults.draftRound || "",
      draftPick: defaults.draftPick || "",
      finalContractYear: defaults.finalContractYear || "",
      eventSide: defaults.status || "",
      eventTeam: defaults.eventTeam || "",
      saveMode: "Both",
    }));
    setPreview(null);
    setIsDirty(false);
  };

  const handleOpenEdit = (item, idx) => {
    const rowKey = getTransactionRowKey(item, idx);
    setEditError("");
    setEditMessage("");
    if (editingTransactionKey === rowKey) {
      setEditingTransactionKey("");
      setEditForm(null);
      return;
    }
    setEditingTransactionKey(rowKey);
    setEditForm(buildTransactionEditForm(item, form.league));
  };

  const handleEditChange = (field, value) => {
    setEditError("");
    setEditMessage("");
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async (saveTarget) => {
    if (!editForm || !editForm.id) {
      setEditError("Missing transaction ID for this row.");
      return;
    }

    setEditError("");
    setEditMessage("");
    setEditSaving(saveTarget);

    const scriptMap = {
      Both: "UpdateCollegePtt",
      Transactions: "UpdateHistory",
      Email: "EmailTransactionsPtt",
    };

    const payload = {
      script: scriptMap[saveTarget] || "UpdateHistory",
      transactionID: editForm.id,
      playerID: activePlayer?.FBXID || playerID || "",
      saveTarget,
      league: form.league,
      emailCode: editForm.emailCode || editForm.original?.emailCode || "",
      teamCode: editForm.teamCode,
      statusCode: editForm.statusCode,
      posCode: editForm.posCode,
      transDate: editForm.transDate,
      teamYear: editForm.teamYear,
      teamSide: normalizeLeague(form.league) === "EVENT" ? editForm.statusCode : editForm.teamSide,
      noteData: editForm.noteData,
    };

    try {
      const user = await getUserFromToken();
      payload.user = user;
      payload.team = "FBX";

      const response = await fetch("https://www.fbxchange.com/ReactPHP/danTransactions.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!result?.success) {
        throw new Error(result?.error || "Unable to save transaction.");
      }

      applyOneLineRow(result.oneLineRow);
      setHistoryData((prev) =>
        prev.map((item) => {
          if (String(getTransactionID(item)) !== String(editForm.id)) return item;
          return {
            ...item,
            ProTeam: editForm.teamCode,
            A_ProTeam: editForm.teamCode,
            ProTeamStatus: editForm.statusCode,
            PRO_POS: editForm.posCode,
            A_ProPos: editForm.posCode,
            ProTeamDate: editForm.transDate,
            TeamYear: editForm.teamYear,
            TeamSide: normalizeLeague(form.league) === "EVENT" ? editForm.statusCode : editForm.teamSide,
            ST_NOTES: editForm.noteData,
          };
        })
      );
      setEditForm((prev) =>
        prev
          ? {
              ...prev,
              original: {
                league: normalizeLeague(form.league),
                emailCode: prev.emailCode || prev.original?.emailCode || "",
                teamCode: prev.teamCode,
                statusCode: prev.statusCode,
                posCode: prev.posCode,
                transDate: prev.transDate,
                teamYear: prev.teamYear,
                teamSide: normalizeLeague(form.league) === "EVENT" ? prev.statusCode : prev.teamSide,
                noteData: prev.noteData,
              },
            }
          : prev
      );
      if (saveTarget === "Both" || saveTarget === "Email" || saveTarget === "Transactions") {
        const savedTo =
          saveTarget === "Email"
            ? "Email/PTT"
            : saveTarget === "Transactions"
              ? "History"
              : "College/PTT";
        const updatedEmails = Number(result?.updatedEmails || 0);
        setEditMessage(`Saved to ${savedTo}. Updated ${updatedEmails} pending email transaction${updatedEmails === 1 ? "" : "s"}.`);
      } else {
        setEditMessage("Saved.");
      }
    } catch (error) {
      setEditError(error.message || "Unable to save transaction.");
    } finally {
      setEditSaving("");
    }
  };

  const handleDeleteEdit = async () => {
    if (!editForm || !editForm.id) {
      setEditError("Missing transaction ID for this row.");
      return;
    }

    setEditError("");
    setEditMessage("");
    setEditSaving("Delete");
    const payload = {
      script: "DeletePtt",
      transactionID: editForm.id,
      playerID: activePlayer?.FBXID || playerID || "",
    };

    try {
      const user = await getUserFromToken();
      payload.user = user;
      payload.team = "FBX";

      const response = await fetch("https://www.fbxchange.com/ReactPHP/danTransactions.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!result?.success) {
        throw new Error(result?.error || "Unable to delete transaction.");
      }

      setHistoryData((prev) => prev.filter((item) => String(getTransactionID(item)) !== String(editForm.id)));
      setEditingTransactionKey("");
      setEditForm(null);
      setEditMessage(result?.deletedEmails ? `Deleted. Removed ${result.deletedEmails} pending email transaction${Number(result.deletedEmails) === 1 ? "" : "s"}.` : "Deleted.");
    } catch (error) {
      setEditError(error.message || "Unable to delete transaction.");
    } finally {
      setEditSaving("");
    }
  };

  const handleResetCollegeLeague = async () => {
    const resetLeague = normalizeLeague(form.league);
    if (!["CFL", "NFL", "UFL24", "EVENT"].includes(resetLeague)) return;

    const displayLeague = resetLeague === "EVENT" ? "Event" : resetLeague;
    if (!window.confirm(`Are you sure you want to remove ${displayLeague} Data from college`)) {
      return;
    }

    setEditError("");
    setEditMessage("");
    setEditSaving("Reset");

    const payload = {
      script: "ResetCollegeLeague",
      playerID: activePlayer?.FBXID || playerID || "",
      league: form.league,
    };

    try {
      const response = await fetch("https://www.fbxchange.com/ReactPHP/danTransactions.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!result?.success) {
        throw new Error(result?.error || `Unable to clear ${form.league}.`);
      }

      applyOneLineRow(result.oneLineRow);
      setEditMessage(`Cleared ${displayLeague} from College.`);
      closeBlueTransactions();
    } catch (error) {
      setEditError(error.message || `Unable to clear ${form.league}.`);
    } finally {
      setEditSaving("");
    }
  };

  const fieldClass = "col-48 px-1 mb-1";
  const inlineFieldClass = `${fieldClass} d-flex align-items-center gap-1`;
  const inlineLabelStyle = { width: "92px", flex: "0 0 92px", whiteSpace: "nowrap", textAlign: "right", paddingRight: "12px", fontWeight: 700 };
  const inputClass = "form-control form-control-sm";
  const selectClass = "form-select form-select-sm fw-bold";
  const transactionGridStyle = {
    display: "grid",
    gridTemplateColumns: "64px 58px 62px 94px 46px 46px minmax(90px, 1fr)",
    alignItems: "center",
    columnGap: "4px",
  };
  const isTransactionEditing = !!editingTransactionKey && !!editForm;
  const transactionFocusFadeStyle = isTransactionEditing
    ? { opacity: 0.35, pointerEvents: "none", filter: "grayscale(0.35)" }
    : undefined;

  return (
    <DragBox2026
      storageKey="FBX_BLUE_TRANSACTION_DRAG"
      defaultPos={{ x: 260, y: 95 }}
      title={playerTitle}
      minWidth={1280}
      maxWidth={1280}
      showReset
      showClose
      closeValue=""
      zIndex={997}
      className="card shadow-sm bb-box"
    >
      <div className="col-48" style={{ maxHeight: "calc(100vh - 185px)", overflowY: "auto" }}>
        <div className="col-48 d-flex flex-column flex-md-row align-items-stretch">
          <div
            ref={leftColumnRef}
            className="pe-md-3"
            style={{ flex: "0 0 450px", width: "450px", maxWidth: "450px", transition: "opacity 160ms ease, filter 160ms ease", ...transactionFocusFadeStyle }}
          >
            <label className={inlineFieldClass}>
              <div className="fw-bold fs-8" style={inlineLabelStyle}>League:</div>
              <select className={selectClass} value={form.league} onChange={(e) => handleLeagueChange(e.target.value)}>
                <option value="">Choose League</option>
                {leagueOptions.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </label>

            <label className={inlineFieldClass}>
              <div className="fw-bold fs-8" style={inlineLabelStyle}>Team:</div>
              <select className={selectClass} value={form.teamCode} onChange={(e) => handleChange("teamCode", e.target.value)}>
                <option value=""></option>
                {teamOptions.map((team) => (
                  <option key={`${team.Affiliation || form.league}-${team.Team}`} value={team.Team}>
                    {team.Team_Name}
                  </option>
                ))}
              </select>
            </label>

            <label className={inlineFieldClass}>
              <div className="fw-bold fs-8" style={inlineLabelStyle}>Status:</div>
              <select
                className={selectClass}
                value={form.statusCode}
                onChange={(e) => handleChange("statusCode", e.target.value)}
              >
                {form.statusCode && selectedStatusLabel && (
                  <option value={form.statusCode} hidden>
                    {selectedStatusLabel}
                  </option>
                )}
                <option value="">--</option>
                {visibleStatusOptions.map((item, idx) => (
                  <option key={`${form.league}-${item.status_id}-${idx}`} value={item.status_id}>
                    {item.status}
                  </option>
                ))}
              </select>
            </label>

            <label className={inlineFieldClass}>
              <div className="fw-bold fs-8" style={inlineLabelStyle}>Position:</div>
              <select className={selectClass} value={form.posCode} onChange={(e) => handleChange("posCode", e.target.value)}>
                <option value=""></option>
                {form.posCode && !positionOptions.some((position) => position.code === form.posCode) && (
                  <option value={form.posCode}>{form.posCode}</option>
                )}
                {positionOptions.map((position) => (
                  <option key={position.code} value={position.code}>
                    {position.label}
                  </option>
                ))}
              </select>
            </label>

            {form.league === "CFL" && (
              <label className={inlineFieldClass}>
                <div className="fw-bold fs-8" style={inlineLabelStyle}>CFL FCY:</div>
                <input className={inputClass} value={form.finalContractYear || ""} onChange={(e) => handleChange("finalContractYear", e.target.value)} />
              </label>
            )}

            {form.league === "Event" && (
              <>
                <label className={inlineFieldClass}>
                  <div className="fw-bold fs-8" style={inlineLabelStyle}>Event Side:</div>
                  <input className={inputClass} value={form.eventSide} onChange={(e) => handleChange("eventSide", e.target.value.toUpperCase())} />
                </label>

                <label className={inlineFieldClass}>
                  <div className="fw-bold fs-8" style={inlineLabelStyle}>Event Year:</div>
                  <input className={inputClass} value={form.eventTeam} onChange={(e) => handleChange("eventTeam", e.target.value)} />
                </label>
              </>
            )}

            <label className={inlineFieldClass}>
              <div className="fw-bold fs-8" style={inlineLabelStyle}>Date:</div>
              <SimpleCalendar
                value={form.transDate}
                onDateChange={(value) => handleChange("transDate", value)}
                allowClear={false}
              />
            </label>

            <label className={inlineFieldClass}>
              <div className="fw-bold fs-8" style={inlineLabelStyle}>Draft Team:</div>
              <select className={selectClass} value={form.draftCode} onChange={(e) => handleChange("draftCode", e.target.value)}>
                <option value=""></option>
                {form.draftCode && !teamOptions.some((team) => team.Team === form.draftCode) && (
                  <option value={form.draftCode}>{form.draftCode}</option>
                )}
                {teamOptions.map((team) => (
                  <option key={`draft-${team.Affiliation || form.league}-${team.Team}`} value={team.Team}>
                    {team.Team_Name}
                  </option>
                ))}
              </select>
            </label>

            <label className={inlineFieldClass}>
              <div className="fw-bold fs-8" style={inlineLabelStyle}>Round:</div>
              <input className={inputClass} value={form.draftRound} onChange={(e) => handleChange("draftRound", e.target.value)} />
            </label>

            <label className={inlineFieldClass}>
              <div className="fw-bold fs-8" style={inlineLabelStyle}>Pick:</div>
              <input className={inputClass} value={form.draftPick} onChange={(e) => handleChange("draftPick", e.target.value)} />
            </label>

          </div>

          <div className="flex-grow-1 ps-md-2 d-flex">
            <div className="border rounded bg-white d-flex flex-column flex-grow-1" style={{ height: `${transactionHeight}px`, maxHeight: `${transactionHeight}px` }}>
              <div className="bg-navy text-lemon px-2 py-1 fw-bold fs-9" style={transactionGridStyle}>
                <div className="text-center">ORG</div>
                <div className="text-center">Team</div>
                <div className="text-center">Status</div>
                <div className="text-center">Date</div>
                <div className="text-center">Pos</div>
                <div className="text-center">Year</div>
                <div className="ps-2">Note</div>
              </div>

              <div className="flex-grow-1" style={{ minHeight: 0, overflowY: "auto" }}>
                {historyLoading && (
                  <div className="text-center text-muted p-3 fs-8">Loading transactions...</div>
                )}

                {!historyLoading && historyError && (
                  <div className="text-center text-danger p-3 fs-8">{historyError}</div>
                )}

                {!historyLoading && !historyError && filteredHistoryData.length === 0 && (
                  <div className="text-center text-muted p-3 fs-8">There are No {form.league} Transactions for this Athlete</div>
                )}

                {!historyLoading && !historyError && filteredHistoryData.map((item, idx) => {
                  const rowKey = getTransactionRowKey(item, idx);
                  const isEditing = editingTransactionKey === rowKey && editForm;
                  const pendingEmailCount = Number(editForm?.pendingEmailCount ?? editForm?.original?.pendingEmailCount ?? 0);
                  const canUpdateEmailTransactions = pendingEmailCount > 0;
                  return (
                    <React.Fragment key={rowKey}>
                      <div
                        className={`border-bottom px-2 py-1 fs-9 text-navy ${idx % 2 ? "bg-light" : ""}`}
                        style={transactionGridStyle}
                      >
                        <div className="text-center">
                          <button
                            type="button"
                            className={`btn btn-sm py-0 px-0 fs-9 w-100 ${isEditing ? "btn-primary" : "btn-outline-primary"}`}
                            title="Edit transaction"
                            onClick={() => handleOpenEdit(item, idx)}
                          >
                            {item.League}
                          </button>
                        </div>
                        <div className="text-center">{cleanDisplayValue(item.A_ProTeam)}</div>
                        <div className="text-center">{cleanDisplayValue(item.ProTeamStatus)}</div>
                        <div className="text-center">{cleanDisplayValue(item.ProTeamDate)}</div>
                        <div className="text-center">{cleanDisplayValue(item.A_ProPos)}</div>
                        <div className="text-center">{cleanDisplayValue(item.TeamYear)}</div>
                        <div
                          className="ps-2 text-truncate"
                          title={cleanDisplayValue(item.ST_NOTES)}
                          style={{ minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                        >
                          {cleanDisplayValue(item.ST_NOTES)}
                        </div>
                      </div>

                      {isEditing && (
                        <div className="border-bottom bg-warning-subtle px-2 py-2 fs-9 text-navy">
                          <div className="d-flex flex-wrap align-items-end">
                            <label className="pe-1 mb-2" style={{ width: "82px" }}>
                              <div className="fw-bold mb-1">Team</div>
                              <select className={selectClass} value={editForm.teamCode} onChange={(e) => handleEditChange("teamCode", e.target.value)}>
                                <option value=""></option>
                                <option value="XXX">XXX</option>
                                {teamOptions.map((team) => (
                                  <option key={`edit-${team.Affiliation || form.league}-${team.Team}`} value={team.Team}>
                                    {team.Team}
                                  </option>
                                ))}
                              </select>
                            </label>

                            <label className="px-1 mb-2" style={{ width: "160px" }}>
                              <div className="fw-bold mb-1">Date</div>
                              <SimpleCalendar
                                value={editForm.transDate}
                                onDateChange={(value) => handleEditChange("transDate", value)}
                                allowClear={false}
                              />
                            </label>

                            <label className="px-1 mb-2" style={{ width: "78px" }}>
                              <div className="fw-bold mb-1">Status</div>
                              <select className={selectClass} value={editForm.statusCode} onChange={(e) => handleEditChange("statusCode", e.target.value)}>
                                <option value="">--</option>
                                {visibleStatusOptions.map((status, idx) => (
                                  <option key={`edit-${form.league}-${status.status_id}-${idx}`} value={status.status_id}>
                                    {status.status_id}
                                  </option>
                                ))}
                              </select>
                            </label>

                            <label className="px-1 mb-2" style={{ width: "76px" }}>
                              <div className="fw-bold mb-1">Year</div>
                              <input className={inputClass} value={editForm.teamYear} onChange={(e) => handleEditChange("teamYear", e.target.value)} />
                            </label>

                            <label className="px-1 mb-2" style={{ width: "86px" }}>
                              <div className="fw-bold mb-1">Pos</div>
                              <select className={selectClass} value={editForm.posCode} onChange={(e) => handleEditChange("posCode", e.target.value)}>
                                <option value=""></option>
                                {editForm.posCode && !positionOptions.some((position) => position.code === editForm.posCode) && (
                                  <option value={editForm.posCode}>{editForm.posCode}</option>
                                )}
                                {positionOptions.map((position) => (
                                  <option key={`edit-${position.code}`} value={position.code}>
                                    {position.code}
                                  </option>
                                ))}
                              </select>
                            </label>

                            <label className="px-1 mb-2 flex-grow-1" style={{ minWidth: "120px" }}>
                              <div className="fw-bold mb-1">Note</div>
                              <input className={inputClass} value={editForm.noteData} onChange={(e) => handleEditChange("noteData", e.target.value)} />
                            </label>
                          </div>

                          {editError && <div className="text-danger fw-bold mb-2">{editError}</div>}
                          {editMessage && <div className="text-success fw-bold mb-2">{editMessage}</div>}

                          <div className="d-flex gap-2 justify-content-end">
                            <button type="button" className="btn btn-info btn-sm fw-bold px-4" style={{ minWidth: "220px" }} disabled={!!editSaving} onClick={() => handleSaveEdit("Transactions")}>
                              {editSaving === "Transactions" ? "Saving..." : "History"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-success btn-sm px-3"
                              disabled={!!editSaving || !canUpdateEmailTransactions}
                              title={canUpdateEmailTransactions ? `Update PTT and ${pendingEmailCount} pending email transaction${pendingEmailCount === 1 ? "" : "s"}` : "No pending unsent email transactions for this transaction"}
                              onClick={() => handleSaveEdit("Email")}
                            >
                              {editSaving === "Email" ? "Saving..." : "Email/PTT"}
                            </button>
                            <button type="button" className="btn btn-warning btn-sm px-3" disabled={!!editSaving} onClick={() => handleSaveEdit("Both")}>
                              {editSaving === "Both" ? "Saving..." : "College/PTT"}
                            </button>
                            <button type="button" className="btn btn-danger btn-sm px-3" disabled={!!editSaving} onClick={handleDeleteEdit}>
                              {editSaving === "Delete" ? "Deleting..." : "Delete"}
                            </button>
                            <button type="button" className="btn btn-outline-secondary btn-sm px-3" disabled={!!editSaving} onClick={() => handleOpenEdit(item, idx)}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

        </div>

        <div className="col-48 px-1 mb-1 d-flex align-items-stretch gap-2">
          <div className="d-flex flex-column align-items-end justify-content-start" style={{ flex: "0 0 72px", paddingRight: "10px" }}>
            <div className="fw-bold fs-8 mb-1">Note</div>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm py-0 px-2"
              onClick={handleClearNote}
              disabled={!form.noteData}
            >
              Clear
            </button>
          </div>
          <textarea className={inputClass} style={{ flex: "1 1 auto", minWidth: 0 }} value={form.noteData} onChange={(e) => handleChange("noteData", e.target.value)} rows="3" />
        </div>

        <div
          className="col-48 d-flex align-items-center gap-2 mt-1"
          style={{ transition: "opacity 160ms ease, filter 160ms ease", ...transactionFocusFadeStyle }}
        >
          <button
            type="button"
            className="btn btn-success btn-sm col-16"
            disabled={topSaving || isTransactionEditing || !form.league || (!activePlayer?.FBXID && !playerID)}
            onClick={handleSaveTopTransaction}
          >
            {topSaving ? "Saving..." : "SAVE BOTH"}
          </button>
          <button
            type="button"
            className="btn btn-warning btn-sm px-4"
            disabled={topSaving || isTransactionEditing || !form.league || (!activePlayer?.FBXID && !playerID)}
            onClick={() => handleSaveTopMode("SaveCollegeOnly", "College")}
          >
            Current
          </button>
          <button
            type="button"
            className="btn btn-info btn-sm px-4"
            disabled={topSaving || isTransactionEditing || !form.league || (!activePlayer?.FBXID && !playerID)}
            onClick={() => handleSaveTopMode("SaveHistoryOnly", "Trans")}
          >
            History
          </button>
          {["CFL", "NFL", "UFL24", "EVENT"].includes(normalizeLeague(form.league)) && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm px-3"
              disabled={topSaving || isTransactionEditing || !!editSaving || !form.league || (!activePlayer?.FBXID && !playerID)}
              onClick={handleResetCollegeLeague}
            >
              {editSaving === "Reset" ? "Clearing..." : `Clear ${normalizeLeague(form.league) === "EVENT" ? "Event" : normalizeLeague(form.league)} from College`}
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary ms-auto"
            disabled={isTransactionEditing}
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>

        {topSaveError && <div className="col-48 alert alert-danger mt-3 mb-0 py-2">{topSaveError}</div>}
        {topSaveMessage && <div className="col-48 alert alert-success mt-3 mb-0 py-2">{topSaveMessage}</div>}

        {preview && (
          <div className="col-48 alert alert-info mt-3 mb-0">
            <div className="fw-bold text-primary mb-2">THIS IS WHAT WOULD SAVE</div>
            <pre className="bg-white border rounded p-2 mb-0 fs-8" style={{ maxHeight: "260px", overflow: "auto", whiteSpace: "pre-wrap" }}>
              {JSON.stringify(preview, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </DragBox2026>
  );
};

export default BlueTransactions;
